import { useState, useEffect, useCallback } from 'react'
import { getCaselawReferenceHtmlForStandaloneViewerV3 } from '../fetch/getCaselawReferenceHtmlForStandaloneViewerV3'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { CaselawReferenceMetadataResponseV3 } from '../schema/caselaw-reference-viewer.schema'

interface UseGetCaselawReferenceHtmlState {
  data: string | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Use Get Caselaw Reference HTML
 * Fetch the HTML for a caselaw reference via the V3 endpoint.
 * @param metadata - The V3 metadata of the caselaw reference, or `null`/`undefined`
 *   while the caller is still loading it. When not yet available, the hook waits in
 *   an idle state without fetching or erroring.
 * @returns The HTML for the given metadata
 */
export function useGetCaselawReferenceHtmlInStandalone(metadata: CaselawReferenceMetadataResponseV3 | null | undefined): UseGetCaselawReferenceHtmlState {
  const [data, setData] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchData = useCallback(async () => {
    if (!metadata) return

    setIsLoading(true)
    setIsError(false)

    try {
      const storageIdentifier = metadata.s3_path
      if (!storageIdentifier) throw new Error('No s3_path found in reference metadata')

      const result = await getCaselawReferenceHtmlForStandaloneViewerV3(storageIdentifier)

      if (!result) {
        throw new Error(`No HTML found for reference metadata` + JSON.stringify(metadata))
      }

      setData(result.data)
      setIsError(false)
    } catch (err) {
      const sentry_transaction_id = nanoid()
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Reference HTML fetch error - detail: ${err}`), {
          extra: {
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            metadata: JSON.stringify(metadata),
          },
        })
      })
      setIsError(true)
      setData(null)
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }, [metadata])

  // Effect to fetch data when version or identifier changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Refetch function
  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    isLoading,
    isError,
    refetch,
  }
}
